import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { btnPrimary, btnGhost } from '../shared/FormKit';
import { sectionTitle, subsectionTitle, subsectionTitleBar } from './constants';

// The one application-form page (docs/workflow-ui.md §4.2): a single max-w-3xl
// column of numbered <FormSection> cards in the fixed order ① pick the subject
// → ② request details → ③ attachments → ④ reason/remarks & declaration, closed
// by a Cancel/Submit row. Field content stays per-flow; only the skeleton is
// shared. One card per step (not one card holding hairline
// separators) — on the gray page the steps read as separate blocks at a glance.
export const FormShell = ({
  backTo,
  backLabel,
  title,
  subtitle,
  error = null,
  onSubmit,
  submitLabel = 'Submit Request',
  submittingLabel = 'Submitting…',
  submitting = false,
  submitDisabled = false,
  // Optional extra buttons on the action row: `actionsLeft` sits at the left
  // edge (destructive things like "Delete draft"), `actionsExtra` between
  // Cancel and Submit ("Save draft"). `notice` prints a green line above the
  // row after a save that keeps the user on the page.
  actionsLeft = null,
  actionsExtra = null,
  notice = null,
  children,
}) => {
  const navigate = useNavigate();
  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" /> {backLabel}</Link>
      <div className="mt-2 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        className="space-y-4"
      >
        {children}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {notice && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{notice}</div>
        )}

        {/* The action row is a card too, so the form has a visible end. */}
        <div className="bg-white rounded-lg border border-slate-200 px-5 py-4 flex items-center gap-2">
          {actionsLeft}
          <div className="flex-1" />
          <button type="button" onClick={() => navigate(backTo)} className={btnGhost}>Cancel</button>
          {actionsExtra}
          <button type="submit" disabled={submitting || submitDisabled} className={btnPrimary}>
            {submitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

// A numbered form section: "① Select the engagement" etc. Own card, tinted
// header strip and a blue step chip so the numbered blocks are unmissable;
// the hint sits under the title (trailing the title inline it would be
// squeezed to nothing on long hints). Children stack with space-y-4.
// No overflow-hidden on the card: it would clip the SearchSelect dropdown.
// The header strip rounds its
// own top corners instead — 7px = the card's 8px radius minus the 1px border.
export const FormSection = ({ step, title, hint = null, children }) => (
  <section className="bg-white rounded-lg border border-slate-200">
    {/* slate-100, not slate-50: the page itself is gray-50, so a lighter strip
        would read as plain white and the step header would vanish. */}
    <div className="flex items-start gap-2.5 px-5 py-3 bg-slate-100 border-b border-slate-200 rounded-t-[7px]">
      <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
        {step}
      </span>
      <div className="min-w-0">
        <h2 className={sectionTitle}>{title}</h2>
        {hint && <p className="text-xs text-slate-500 mt-0.5 break-words">{hint}</p>}
      </div>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </section>
);

/**
 * A titled block INSIDE a section, card or modal (docs/workflow-ui.md:
 * `subsectionTitle` + short blue bar) — one size below the section title, and
 * separated from the block above by a rule, so a long form reads as distinct
 * parts. `first` drops the rule for the opening block.
 */
export const SubSection = ({ title, hint = null, first = false, children }) => (
  <div className={`${first ? '' : 'border-t border-slate-200 pt-4 '}space-y-4`}>
    <div>
      <h4 className={subsectionTitle}>
        <span className={subsectionTitleBar} aria-hidden="true" />
        {title}
      </h4>
      {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
    </div>
    {children}
  </div>
);

export default FormShell;
